/** Canonical requirements shared by CRM navigation and direct-page access. */
export function crmPagePermissions(path: string): readonly string[] | null {
  if (path === '/crm/so/im' || path.startsWith('/crm/so/im/')) {
    return path.endsWith('/create') ? ['im.view', 'im.create'] : ['im.view'];
  }
  const soPage = soPagePermissions(path);
  if (soPage) return soPage;
  if (path === '/crm') return ['crm.dashboard.view'];
  if (path === '/crm/activity-logs') return ['crm.activity_logs.view'];
  if (path === '/crm/assignments' || path === '/crm/approvals') return ['crm.assignments.view'];
  if (path === '/crm/pipeline') return ['crm.pipeline.view'];
  if (path === '/crm/analyst-workplace' || path.startsWith('/crm/analyst-workplace/')) return ['crm.analyst_workspace.view'];

  for (const resource of ['contacts', 'customers', 'opportunities', 'partners', 'products']) {
    const base = `/crm/${resource}`;
    if (path !== base && !path.startsWith(`${base}/`)) continue;
    const requirements = [`crm.${resource}.view`];
    const isEditPage = ['opportunities', 'partners', 'products'].includes(resource)
      && /^\d+$/.test(path.slice(base.length + 1));
    const action = isEditPage ? 'update' : path.endsWith('/create') ? 'create'
      : path.endsWith('/edit') ? 'update'
      : path.endsWith('/import') ? 'import'
      : path.endsWith('/export') ? 'export' : null;
    if (action) requirements.push(`crm.${resource}.${action}`);
    return requirements;
  }

  return null;
}

/** Sales Order workspace tabs and the permission that unlocks each one. */
export const SALES_ORDER_TAB_PERMISSIONS: Record<string, string> = {
  '': 'so.sales_orders.view',
  '/dealsheet': 'so.purchase_orders.view',
  '/po': 'so.purchase_orders.view',
  '/internal-memo': 'im.view',
  '/gpi': 'so.gpi.view',
  '/timeline': 'so.timeline.view',
  '/scurve': 'so.scurve.view',
  '/change-requests': 'so.sales_orders.view',
  '/version-history': 'so.sales_orders.view',
  '/approval': 'so.approval.view',
};

/** Sales Orders, Purchase Orders and WBS Templates pages (menus under CRM). */
function soPagePermissions(path: string): readonly string[] | null {
  const salesOrderTab = path.match(/^\/crm\/sales-orders\/\d+(\/[a-z-]+)?/);
  if (salesOrderTab) {
    const tab = SALES_ORDER_TAB_PERMISSIONS[salesOrderTab[1] ?? ''];
    return tab && tab !== 'so.sales_orders.view' ? ['so.sales_orders.view', tab] : ['so.sales_orders.view'];
  }
  if (path === '/crm/sales-orders' || path.startsWith('/crm/sales-orders/')) return ['so.sales_orders.view'];

  // The PO form pages create (no ?id) or edit (?id=); they check create/update themselves.
  if (path === '/po' || path === '/po/create' || path.startsWith('/crm/so/po')) return ['so.purchase_orders.view'];

  if (path === '/crm/wbs-templates' || path.startsWith('/crm/wbs-templates/')) return ['so.wbs_templates.view'];

  return null;
}
